import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import AuthContext from '../../../../Context/AuthProvider';
import "./Payroll.css";

// Helper function to calculate payroll values
const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus, napsa, nhima) => {
  return salary - repayments - deductions + bonus - napsa - nhima;
};

// Send payroll data to Node.js server and handle email and saving to DB
const sendPayrollDataToServer = async (payrollData) => {
  try {
    // Send payroll data to the backend (Node.js)
    const response = await axios.post('http://localhost:5000/api/payroll', { payrollData });
    console.log('Data sent successfully:', response.data);
  } catch (error) {
    console.error('Error sending payroll data:', error);
  }
};

const PayrollPage = () => {
  const axiosPrivate = useAxiosPrivate();
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [dateRange, setDateRange] = useState({ month: '', year: '' });
  const { setAuth, auth } = useContext(AuthContext);

  // Fetch users data
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const getUsers = async () => {
      try {
        const response = await axiosPrivate.get('/users', {
          signal: controller.signal,
          params: {
            Org: auth.foundUser.Org,
          },
        });
        if (isMounted) {
          setUsers(response.data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    getUsers();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [auth]);

  // Fetch transactions data
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const getTransactions = async () => {
      try {
        const response = await axiosPrivate.get('/txns', {
          signal: controller.signal,
          params: {
            Org: auth.foundUser.Org,
          },
        });
        if (isMounted) {
          setTransactions(response.data.propdata);
        }
      } catch (err) {
        console.error(err);
      }
    };

    getTransactions();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [auth]);

  // Process data to calculate payroll after both users and transactions are loaded
  useEffect(() => {
    if (users.length > 0 && transactions.length > 0) {
      const payroll = users.map(user => {
        // Filter transactions by user ID and date range (month and year)
        const userTransactions = transactions.filter(transaction => {
          const isUserTransaction = transaction.txnID == user._id;
          if (!dateRange.month || !dateRange.year) return isUserTransaction;

          const transactionDate = new Date(transaction.TDate);
          const transactionYear = transactionDate.getFullYear();
          const transactionMonth = transactionDate.getMonth() + 1;

          return isUserTransaction && transactionYear == dateRange.year && transactionMonth == dateRange.month;
        });

        const repaymentThisMonth = userTransactions
          .filter(transaction => transaction.transactionType === 'Deduction Repayment')
          .reduce((total, transaction) => total + transaction.transactionAmount, 0);

        const deductionsThisMonth = userTransactions
          .filter(transaction => transaction.transactionType === 'Deduction')
          .reduce((total, transaction) => total + transaction.transactionAmount, 0);

        const bonusesThisMonth = userTransactions
          .filter(transaction => transaction.transactionType === 'Allowance')
          .reduce((total, transaction) => total + transaction.amount, 0);

        const napsaDeduction = user.Base_Pay * 0.05;  // 5% NAPSA
        const nhimaDeduction = user.Base_Pay * 0.01; // 1% NHIMA

        const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth, napsaDeduction, nhimaDeduction);

        return {
          userId: user.userId,
          name: user.name,
          salary: user.Base_Pay,
          broughtForward: user.Advance_BF,
          repayment: repaymentThisMonth,
          deductions: deductionsThisMonth,
          bonus: bonusesThisMonth,
          napsa: napsaDeduction,
          nhima: nhimaDeduction,
          netPay,
          email: user.email
        };
      });

      setPayrollData(payroll);
    }
  }, [users, transactions, dateRange]);

  const totalPay = payrollData.reduce((total, employee) => total + employee.netPay, 0);

  // Function to download CSV
  const downloadCSV = () => {
    const csv = Papa.unparse(payrollData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'payroll.csv';
    link.click();
  };

  // Function to download PDF
  const downloadPDF = () => {
    const { jsPDF } = require('jspdf');
    const doc = new jsPDF();
    doc.autoTable({
      head: [['Name', 'Salary', 'Brought Forward', 'Deduction Repayment', 'Deductions', 'Bonus', 'NAPSA', 'NHIMA', 'Net Pay']],
      body: payrollData.map(employee => [
        employee.name,
        employee.salary,
        employee.broughtForward,
        employee.repayment,
        employee.deductions,
        employee.bonus,
        employee.napsa,
        employee.nhima,
        employee.netPay,
      ]),
    });
    doc.save('payroll.pdf');
  };

  // Function to send payroll data to the backend
  const sendPayrollData = () => {
    sendPayrollDataToServer(payrollData);
  };

  return (
    <div className="payroll-page">
      <h1>Payroll</h1>

      <div className="payroll-header">
        <div className="download-buttons">
          <button onClick={downloadCSV}>Download CSV</button>
          <button onClick={downloadPDF}>Download PDF</button>
          <button onClick={sendPayrollData}>Send Payroll</button>
        </div>
        <div className="total-pay">
          Total Pay: {totalPay}
        </div>
      </div>

      {/* Date Range Filters */}
      <div>
        <input
          type="month"
          value={dateRange.month ? `${dateRange.year}-${String(dateRange.month).padStart(2, '0')}` : ''}
          onChange={(e) => {
            const [year, month] = e.target.value.split('-');
            setDateRange({ year, month: parseInt(month) });
          }}
          placeholder="Select Month"
        />
        <input
          type="number"
          value={dateRange.year}
          onChange={(e) => setDateRange({ ...dateRange, year: e.target.value })}
          placeholder="Select Year"
        />
      </div>

      {/* Payroll Table */}
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Salary</th>
            <th>Brought Forward</th>
            <th>Deduction Repayment</th>
            <th>Deductions</th>
            <th>Bonus</th>
            <th>NAPSA</th>
            <th>NHIMA</th>
            <th>Net Pay</th>
          </tr>
        </thead>
        <tbody>
          {payrollData.map((employee) => (
            <tr key={employee.userId}>
              <td>{employee.name}</td>
              <td>{employee.salary}</td>
              <td>{employee.broughtForward}</td>
              <td>{employee.repayment}</td>
              <td>{employee.deductions}</td>
              <td>{employee.bonus}</td>
              <td>{employee.napsa}</td>
              <td>{employee.nhima}</td>
              <td>{employee.netPay}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PayrollPage;

// import React, { useState, useEffect, useContext } from 'react';
// import Papa from 'papaparse';
// import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// import AuthContext from '../../../../Context/AuthProvider';
// import "./Payroll.css";

// // Helper function to calculate payroll values
// const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus, tax) => {
//   return salary - repayments - deductions - tax + bonus;  // Adjust formula to account for tax
// }

// // Helper function to calculate PAYE tax
// const calculatePAYETax = (chargeableIncome) => {
//   let tax = 0;

//   // Define PAYE tax bands
//   if (chargeableIncome <= 5100) {
//     tax = 0;
//   } else if (chargeableIncome <= 7100) {
//     tax = (chargeableIncome - 5100) * 0.20;
//   } else if (chargeableIncome <= 9200) {
//     tax = (chargeableIncome - 7100) * 0.30 + (7100 - 5100) * 0.20;
//   } else {
//     tax = (chargeableIncome - 9200) * 0.37 + (9200 - 7100) * 0.30 + (7100 - 5100) * 0.20;
//   }

//   return tax;
// }

// const PayrollPage = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const [users, setUsers] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [payrollData, setPayrollData] = useState([]);
//   const [dateRange, setDateRange] = useState({ month: '', year: '' });
//   const { setAuth, auth } = useContext(AuthContext);

//   // Fetch users data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getUsers = async () => {
//       try {
//         const response = await axiosPrivate.get('/users', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });
//         if (isMounted) {
//           setUsers(response.data);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getUsers();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Fetch transactions data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getTransactions = async () => {
//       try {
//         const response = await axiosPrivate.get('/txns', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });

//         console.log("res",response)
//         if (isMounted) {
//           setTransactions(response.data.propdata);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getTransactions();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Process data to calculate payroll after both users and transactions are loaded
//   useEffect(() => {
//     if (users.length > 0 && transactions.length > 0) {
//       const payroll = users.map(user => {
//         // Filter transactions by user ID and date range (month and year)
//         const userTransactions = transactions.filter(transaction => {
//           // Ensure the transaction belongs to the user
//           const isUserTransaction = transaction.txnID == user._id;

//           // If no date range is selected, return all transactions
//           if (!dateRange.month || !dateRange.year) {
//             console.log("invalid date")
//             return isUserTransaction;
//           }

//           // Get the year and month of the transaction date (assuming it's in the format YYYY-MM-DD)
//           const transactionDate = new Date(transaction.TDate); // Modify this if the format is different
//           const transactionYear = transactionDate.getFullYear();
//           const transactionMonth = transactionDate.getMonth() + 1; // Month is 0-indexed in JavaScript

//           // Check if the transaction date matches the selected month and year
//           return isUserTransaction && transactionYear == dateRange.year && transactionMonth == dateRange.month;
//         });

//         // Sum up the repayment (deduction repayments are positive amounts)
//         const repaymentThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction Repayment')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up the deductions (deductions are negative amounts)
//         const deductionsThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up bonuses
//         const bonusesThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Allowance')
//           .reduce((total, transaction) => total + transaction.amount, 0);

//         // Calculate chargeable income (this could be salary + bonus - deductions)
//         const chargeableIncome = user.Base_Pay + bonusesThisMonth - deductionsThisMonth;

//         // Calculate PAYE tax
//         const tax = calculatePAYETax(chargeableIncome);

//         // Calculate net pay
//         const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth, tax);

//         return {
//           userId: user.userId,
//           name: user.name,
//           salary: user.Base_Pay,
//           broughtForward: user.Advance_BF,
//           repayment: repaymentThisMonth,
//           deductions: deductionsThisMonth,
//           bonus: bonusesThisMonth,
//           tax: tax,
//           netPay
//         };
//       });

//       setPayrollData(payroll);
//     }
//   }, [users, transactions, dateRange]);

//   const totalPay = payrollData.reduce((total, employee) => total + employee.netPay, 0);

//   // Function to download CSV
//   const downloadCSV = () => {
//     const csv = Papa.unparse(payrollData);  // Convert payroll data to CSV
//     const blob = new Blob([csv], { type: 'text/csv' });
//     const link = document.createElement('a');
//     link.href = URL.createObjectURL(blob);
//     link.download = 'payroll.csv';  // Filename for CSV file
//     link.click();
//   };

//   // Function to download PDF (if needed)
//   const downloadPDF = () => {
//     const { jsPDF } = require('jspdf');
//     const doc = new jsPDF();
//     doc.autoTable({
//       head: [['Name', 'Salary', 'Brought Forward', 'Deduction Repayment', 'Deductions', 'Bonus', 'Tax', 'Net Pay']],
//       body: payrollData.map(employee => [
//         employee.name,
//         employee.salary,
//         employee.broughtForward,
//         employee.repayment,
//         employee.deductions,
//         employee.bonus,
//         employee.tax,
//         employee.netPay,
//       ]),
//     });
//     doc.save('payroll.pdf');
//   };

//   return (
//     <div className="payroll-page">
//       <h1>Payroll</h1>

//       <div className="payroll-header">
//         <div className="download-buttons">
//           <button onClick={downloadCSV}>Download CSV</button>
//           <button onClick={downloadPDF}>Download PDF</button>
//         </div>
//         <div className="total-pay">
//           Total Pay: {totalPay}
//         </div>
//       </div>

//       {/* Date Range Filters */}
//       <div>
//         <input
//           type="month"
//           value={dateRange.month ? `${dateRange.year}-${String(dateRange.month).padStart(2, '0')}` : ''}
//           onChange={(e) => {
//             const [year, month] = e.target.value.split('-');
//             setDateRange({ year, month: parseInt(month) });
//           }}
//           placeholder="Select Month"
//         />
//         <input
//           type="number"
//           value={dateRange.year}
//           onChange={(e) => setDateRange({ ...dateRange, year: e.target.value })}
//           placeholder="Select Year"
//         />
//       </div>

//       {/* Payroll Table */}
//       <table>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Salary</th>
//             <th>Brought Forward</th>
//             <th>Deduction Repayment</th>
//             <th>Deductions</th>
//             <th>Bonus</th>
//             <th>Tax</th>
//             <th>Net Pay</th>
//           </tr>
//         </thead>
//         <tbody>
//           {payrollData.map((employee) => (
//             <tr key={employee.userId}>
//               <td>{employee.name}</td>
//               <td>{employee.salary}</td>
//               <td>{employee.broughtForward}</td>
//               <td>{employee.repayment}</td>
//               <td>{employee.deductions}</td>
//               <td>{employee.bonus}</td>
//               <td>{employee.tax}</td>
//               <td>{employee.netPay}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// export default PayrollPage;

// import React, { useState, useEffect, useContext } from 'react';
// import Papa from 'papaparse';
// import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// import AuthContext from '../../../../Context/AuthProvider';
// import "./Payroll.css";

// // Helper function to calculate payroll values
// const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus) => {
//   return salary - repayments; // Adjust formula if needed
// }

// const PayrollPage = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const [users, setUsers] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [payrollData, setPayrollData] = useState([]);
//   const [dateRange, setDateRange] = useState({ month: '', year: '' });
//   const { setAuth, auth } = useContext(AuthContext);

//   // Fetch users data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getUsers = async () => {
//       try {
//         const response = await axiosPrivate.get('/users', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });
//         if (isMounted) {
//           setUsers(response.data);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getUsers();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Fetch transactions data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getTransactions = async () => {
//       try {
//         const response = await axiosPrivate.get('/txns', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });

//         console.log("res",response)
//         if (isMounted) {
//           setTransactions(response.data.propdata);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getTransactions();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Process data to calculate payroll after both users and transactions are loaded
//   useEffect(() => {
//     if (users.length > 0 && transactions.length > 0) {
//       const payroll = users.map(user => {
//         // Filter transactions by user ID and date range (month and year)
//         const userTransactions = transactions.filter(transaction => {
//           // Ensure the transaction belongs to the user
//           const isUserTransaction = transaction.txnID == user._id;

//           // If no date range is selected, return all transactions
//           if (!dateRange.month || !dateRange.year) {
//             console.log("invalid date")
//             return isUserTransaction;
//           }

//           // Get the year and month of the transaction date (assuming it's in the format YYYY-MM-DD)
//           const transactionDate = new Date(transaction.TDate); // Modify this if the format is different
//           const transactionYear = transactionDate.getFullYear();
//           const transactionMonth = transactionDate.getMonth() + 1; // Month is 0-indexed in JavaScript

//           // Check if the transaction date matches the selected month and year
//           return isUserTransaction && transactionYear == dateRange.year && transactionMonth == dateRange.month;
//         });

//         // Sum up the repayment (deduction repayments are positive amounts)
//         const repaymentThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction Repayment')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up the deductions (deductions are negative amounts)
//         const deductionsThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up bonuses
//         const bonusesThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Allowance')
//           .reduce((total, transaction) => total + transaction.amount, 0);

//         // Calculate net pay
//         const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth);

//         return {
//           userId: user.userId,
//           name: user.name,
//           salary: user.Base_Pay,
//           broughtForward: user.Advance_BF,
//           repayment: repaymentThisMonth,
//           deductions: deductionsThisMonth,
//           bonus: bonusesThisMonth,
//           netPay
//         };
//       });

//       setPayrollData(payroll);
//     }
//   }, [users, transactions, dateRange]);

//   const totalPay = payrollData.reduce((total, employee) => total + employee.netPay, 0);

//   // Function to download CSV
//   const downloadCSV = () => {
//     const csv = Papa.unparse(payrollData);  // Convert payroll data to CSV
//     const blob = new Blob([csv], { type: 'text/csv' });
//     const link = document.createElement('a');
//     link.href = URL.createObjectURL(blob);
//     link.download = 'payroll.csv';  // Filename for CSV file
//     link.click();
//   };

//   // Function to download PDF (if needed)
//   const downloadPDF = () => {
//     const { jsPDF } = require('jspdf');
//     const doc = new jsPDF();
//     doc.autoTable({
//       head: [['Name', 'Salary', 'Brought Forward', 'Deduction Repayment', 'Deductions', 'Bonus', 'Net Pay']],
//       body: payrollData.map(employee => [
//         employee.name,
//         employee.salary,
//         employee.broughtForward,
//         employee.repayment,
//         employee.deductions,
//         employee.bonus,
//         employee.netPay,
//       ]),
//     });
//     doc.save('payroll.pdf');
//   };

//   return (
//     <div className="payroll-page">
//       <h1>Payroll</h1>

//       <div className="payroll-header">
//         <div className="download-buttons">
//           <button onClick={downloadCSV}>Download CSV</button>
//           <button onClick={downloadPDF}>Download PDF</button>
//         </div>
//         <div className="total-pay">
//           Total Pay: {totalPay}
//         </div>
//       </div>

//       {/* Date Range Filters */}
//       <div>
//         <input
//           type="month"
//           value={dateRange.month ? `${dateRange.year}-${String(dateRange.month).padStart(2, '0')}` : ''}
//           onChange={(e) => {
//             const [year, month] = e.target.value.split('-');
//             setDateRange({ year, month: parseInt(month) });
//           }}
//           placeholder="Select Month"
//         />
//         <input
//           type="number"
//           value={dateRange.year}
//           onChange={(e) => setDateRange({ ...dateRange, year: e.target.value })}
//           placeholder="Select Year"
//         />
//       </div>

//       {/* Payroll Table */}
//       <table>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Salary</th>
//             <th>Brought Forward</th>
//             <th>Deduction Repayment</th>
//             <th>Deductions</th>
//             <th>Bonus</th>
//             <th>Net Pay</th>
//           </tr>
//         </thead>
//         <tbody>
//           {payrollData.map((employee) => (
//             <tr key={employee.userId}>
//               <td>{employee.name}</td>
//               <td>{employee.salary}</td>
//               <td>{employee.broughtForward}</td>
//               <td>{employee.repayment}</td>
//               <td>{employee.deductions}</td>
//               <td>{employee.bonus}</td>
//               <td>{employee.netPay}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// export default PayrollPage;


// import React, { useState, useEffect, useContext } from 'react';
// import Papa from 'papaparse';
// import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// import AuthContext from '../../../../Context/AuthProvider';
// import "./Payroll.css";

// // Helper function to calculate payroll values
// const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus) => {
//   return salary - repayments; // Adjust formula if needed
// }

// const PayrollPage = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const [users, setUsers] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [payrollData, setPayrollData] = useState([]);
//   const [dateRange, setDateRange] = useState({ month: '', year: '' });
//   const { setAuth, auth } = useContext(AuthContext);

//   // Fetch users data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getUsers = async () => {
//       try {
//         const response = await axiosPrivate.get('/users', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });
//         if (isMounted) {
//           setUsers(response.data);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getUsers();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Fetch transactions data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getTransactions = async () => {
//       try {
//         const response = await axiosPrivate.get('/txns', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });

//         console.log("res",response)
//         if (isMounted) {
//           setTransactions(response.data.propdata);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getTransactions();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Process data to calculate payroll after both users and transactions are loaded
//   useEffect(() => {
//     if (users.length > 0 && transactions.length > 0) {
//       const payroll = users.map(user => {
//         // Filter transactions by user ID and date range (month and year)
//         const userTransactions = transactions.filter(transaction => {
//           // Ensure the transaction belongs to the user
//           const isUserTransaction = transaction.txnID == user._id;

//           // If no date range is selected, return all transactions
//           if (!dateRange.month || !dateRange.year) {
//             console.log("invalid date")
//             return isUserTransaction;
//           }

//           // Get the year and month of the transaction date (assuming it's in the format YYYY-MM-DD)
//           const transactionDate = new Date(transaction.TDate); // Modify this if the format is different
//           const transactionYear = transactionDate.getFullYear();
//           const transactionMonth = transactionDate.getMonth() + 1; // Month is 0-indexed in JavaScript

//           // Check if the transaction date matches the selected month and year
//           return isUserTransaction && transactionYear == dateRange.year && transactionMonth == dateRange.month;
//         });

//         // Sum up the repayment (deduction repayments are positive amounts)
//         const repaymentThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction Repayment')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up the deductions (deductions are negative amounts)
//         const deductionsThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up bonuses
//         const bonusesThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Allowance')
//           .reduce((total, transaction) => total + transaction.amount, 0);

//         // Calculate net pay
//         const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth);

//         return {
//           userId: user.userId,
//           name: user.name,
//           salary: user.Base_Pay,
//           broughtForward: user.Advance_BF,
//           repayment: repaymentThisMonth,
//           deductions: deductionsThisMonth,
//           bonus: bonusesThisMonth,
//           netPay
//         };
//       });

//       setPayrollData(payroll);
//     }
//   }, [users, transactions, dateRange]);

//   const totalPay = payrollData.reduce((total, employee) => total + employee.netPay, 0);

//   // Function to download CSV
//   const downloadCSV = () => {
//     const csv = Papa.unparse(payrollData);  // Convert payroll data to CSV
//     const blob = new Blob([csv], { type: 'text/csv' });
//     const link = document.createElement('a');
//     link.href = URL.createObjectURL(blob);
//     link.download = 'payroll.csv';  // Filename for CSV file
//     link.click();
//   };

//   // Function to download PDF (if needed)
//   const downloadPDF = () => {
//     const { jsPDF } = require('jspdf');
//     const doc = new jsPDF();
//     doc.autoTable({
//       head: [['Name', 'Salary', 'Brought Forward', 'Deduction Repayment', 'Deductions', 'Bonus', 'Net Pay']],
//       body: payrollData.map(employee => [
//         employee.name,
//         employee.salary,
//         employee.broughtForward,
//         employee.repayment,
//         employee.deductions,
//         employee.bonus,
//         employee.netPay,
//       ]),
//     });
//     doc.save('payroll.pdf');
//   };

//   return (
//     <div className="payroll-page">
//       <h1>Payroll</h1>

//       {/* Date Range Filters */}
//       <input
//         type="month"
//         value={dateRange.month ? `${dateRange.year}-${String(dateRange.month).padStart(2, '0')}` : ''}
//         onChange={(e) => {
//           const [year, month] = e.target.value.split('-');
//           setDateRange({ year, month: parseInt(month) });
//         }}
//         placeholder="Select Month"
//       />
//       <input
//         type="number"
//         value={dateRange.year}
//         onChange={(e) => setDateRange({ ...dateRange, year: e.target.value })}
//         placeholder="Select Year"
//       />

//       {/* Payroll Table */}
//       <table>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Salary</th>
//             <th>Brought Forward</th>
//             <th>Deduction Repayment</th>
//             <th>Deductions</th>
//             <th>Bonus</th>
//             <th>Net Pay</th>
//           </tr>
//         </thead>
//         <tbody>
//           {payrollData.map((employee) => (
//             <tr key={employee.userId}>
//               <td>{employee.name}</td>
//               <td>{employee.salary}</td>
//               <td>{employee.broughtForward}</td>
//               <td>{employee.repayment}</td>
//               <td>{employee.deductions}</td>
//               <td>{employee.bonus}</td>
//               <td>{employee.netPay}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Total Pay */}
//       <div>Total Pay: {totalPay}</div>

//       {/* Buttons for CSV and PDF Export */}
//       <button onClick={downloadCSV}>Download CSV</button>
//       <button onClick={downloadPDF}>Download PDF</button>
//     </div>
//   );
// };

// export default PayrollPage;

// import React, { useState, useEffect, useContext } from 'react';
// import Papa from 'papaparse';
// import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// import AuthContext from '../../../../Context/AuthProvider';
// import "./Payroll.css";

// // Helper function to calculate payroll values
// const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus) => {
//   return salary - repayments; // Adjust formula if needed
// }

// const PayrollPage = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const [users, setUsers] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [payrollData, setPayrollData] = useState([]);
//   const [dateRange, setDateRange] = useState({ month: '', year: '' });
//   const { setAuth, auth } = useContext(AuthContext);

//   // Fetch users data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getUsers = async () => {
//       try {
//         const response = await axiosPrivate.get('/users', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });
//         if (isMounted) {
//           setUsers(response.data);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getUsers();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Fetch transactions data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getTransactions = async () => {
//       try {
//         const response = await axiosPrivate.get('/txns', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });

//         console.log("res",response)
//         if (isMounted) {
//           setTransactions(response.data.propdata);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getTransactions();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Process data to calculate payroll after both users and transactions are loaded
//   useEffect(() => {
//     if (users.length > 0 && transactions.length > 0) {
//       const payroll = users.map(user => {
//         // Filter transactions by user ID and date range (month and year)
//         const userTransactions = transactions.filter(transaction => {
//           // Ensure the transaction belongs to the user
//           const isUserTransaction = transaction.txnID == user._id;

//           // console.log(" date",dateRange)

//           // If no date range is selected, return all transactions
//           if (!dateRange.month || !dateRange.year) {
//             console.log("invalid date")
//             return isUserTransaction;
//           }

//           // Get the year and month of the transaction date (assuming it's in the format YYYY-MM-DD)
//           const transactionDate = new Date(transaction.TDate); // Modify this if the format is different
//           const transactionYear = transactionDate.getFullYear();
//           const transactionMonth = transactionDate.getMonth() + 1; // Month is 0-indexed in JavaScript

//           // Check if the transaction date matches the selected month and year
//           return isUserTransaction && transactionYear == dateRange.year && transactionMonth == dateRange.month;
//         });

//         // Sum up the repayment (deduction repayments are positive amounts)
//         const repaymentThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction Repayment')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up the deductions (deductions are negative amounts)
//         const deductionsThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up bonuses
//         const bonusesThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Allowance')
//           .reduce((total, transaction) => total + transaction.amount, 0);

//         // Calculate net pay
//         const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth);

//         return {
//           userId: user.userId,
//           name: user.name,
//           salary: user.Base_Pay,
//           broughtForward: user.Advance_BF,
//           repayment: repaymentThisMonth,
//           deductions: deductionsThisMonth,
//           bonus: bonusesThisMonth,
//           netPay
//         };
//       });

//       setPayrollData(payroll);
//     }
//   }, [users, transactions, dateRange]);

//   const totalPay = payrollData.reduce((total, employee) => total + employee.netPay, 0);

//   // Function to download CSV
//   const downloadCSV = () => {
//     const csv = Papa.unparse(payrollData);  // Convert payroll data to CSV
//     const blob = new Blob([csv], { type: 'text/csv' });
//     const link = document.createElement('a');
//     link.href = URL.createObjectURL(blob);
//     link.download = 'payroll.csv';  // Filename for CSV file
//     link.click();
//   };

//   // Function to download PDF (if needed)
//   const downloadPDF = () => {
//     const { jsPDF } = require('jspdf');
//     const doc = new jsPDF();
//     doc.autoTable({
//       head: [['Name', 'Salary', 'Brought Forward', 'Deduction Repayment', 'Deductions', 'Bonus', 'Net Pay']],
//       body: payrollData.map(employee => [
//         employee.name,
//         employee.salary,
//         employee.broughtForward,
//         employee.repayment,
//         employee.deductions,
//         employee.bonus,
//         employee.netPay,
//       ]),
//     });
//     doc.save('payroll.pdf');
//   };

//   return (
//     <div className="payroll-page">
//       <h1>Payroll</h1>

//       {/* Date Range Filters */}
//       <input
//         type="month"
//         value={dateRange.month}
//         onChange={(e) => setDateRange({ ...dateRange, month: e.target.value })}
//         placeholder="Select Month"
//       />
//       <input
//         type="number"
//         value={dateRange.year}
//         onChange={(e) => setDateRange({ ...dateRange, year: e.target.value })}
//         placeholder="Select Year"
//       />

//       {/* Payroll Table */}
//       <table>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Salary</th>
//             <th>Brought Forward</th>
//             <th>Deduction Repayment</th>
//             <th>Deductions</th>
//             <th>Bonus</th>
//             <th>Net Pay</th>
//           </tr>
//         </thead>
//         <tbody>
//           {payrollData.map((employee) => (
//             <tr key={employee.userId}>
//               <td>{employee.name}</td>
//               <td>{employee.salary}</td>
//               <td>{employee.broughtForward}</td>
//               <td>{employee.repayment}</td>
//               <td>{employee.deductions}</td>
//               <td>{employee.bonus}</td>
//               <td>{employee.netPay}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Total Pay */}
//       <div>Total Pay: {totalPay}</div>

//       {/* Buttons for CSV and PDF Export */}
//       <button onClick={downloadCSV}>Download CSV</button>
//       <button onClick={downloadPDF}>Download PDF</button>
//     </div>
//   );
// };

// export default PayrollPage;

// import React, { useState, useEffect, useContext } from 'react';
// import Papa from 'papaparse';
// import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// import AuthContext from '../../../../Context/AuthProvider';
// import "./Payroll.css";

// // Helper function to calculate payroll values
// const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus) => {
//   return salary - repayments - deductions + bonus; // Adjust formula if needed
// }

// const PayrollPage = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const [users, setUsers] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [payrollData, setPayrollData] = useState([]);
//   const [dateRange, setDateRange] = useState({ month: '', year: '' });
//   const { setAuth, auth } = useContext(AuthContext);

//   // Fetch users data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getUsers = async () => {
//       try {
//         const response = await axiosPrivate.get('/users', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });
//         if (isMounted) {
//           setUsers(response.data);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getUsers();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Fetch transactions data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getTransactions = async () => {
//       try {
//         const response = await axiosPrivate.get('/txns', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org,
//           },
//         });
//         if (isMounted) {
//           setTransactions(response.data.propdata);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getTransactions();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Process data to calculate payroll after both users and transactions are loaded
//   useEffect(() => {
//     if (users.length > 0 && transactions.length > 0) {
//       const payroll = users.map(user => {
//         const userTransactions = transactions.filter(transaction => transaction.txnID == user._id);

//         // Sum up the repayment (deduction repayments are positive amounts)
//         const repaymentThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction Repayment')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up the deductions (deductions are negative amounts)
//         const deductionsThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up bonuses
//         const bonusesThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Allowance')
//           .reduce((total, transaction) => total + transaction.amount, 0);

//         // Calculate net pay
//         const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth);

//         return {
//           userId: user.userId,
//           name: user.name,
//           salary: user.Base_Pay,
//           broughtForward: user.Advance_BF,
//           repayment: repaymentThisMonth,
//           deductions: deductionsThisMonth,
//           bonus: bonusesThisMonth,
//           netPay
//         };
//       });

//       setPayrollData(payroll);
//     }
//   }, [users, transactions, dateRange]);

//   const totalPay = payrollData.reduce((total, employee) => total + employee.netPay, 0);

//   // Function to download CSV
//   const downloadCSV = () => {
//     const csv = Papa.unparse(payrollData);  // Convert payroll data to CSV
//     const blob = new Blob([csv], { type: 'text/csv' });
//     const link = document.createElement('a');
//     link.href = URL.createObjectURL(blob);
//     link.download = 'payroll.csv';  // Filename for CSV file
//     link.click();
//   };

//   // Function to download PDF (if needed)
//   const downloadPDF = () => {
//     const { jsPDF } = require('jspdf');
//     const doc = new jsPDF();
//     doc.autoTable({
//       head: [['Name', 'Salary', 'Brought Forward', 'Deduction Repayment', 'Deductions', 'Bonus', 'Net Pay']],
//       body: payrollData.map(employee => [
//         employee.name,
//         employee.salary,
//         employee.broughtForward,
//         employee.repayment,
//         employee.deductions,
//         employee.bonus,
//         employee.netPay,
//       ]),
//     });
//     doc.save('payroll.pdf');
//   };

//   return (
//     <div className="payroll-page">
//       <h1>Payroll</h1>

//       {/* Date Range Filters */}
//       <input
//         type="month"
//         value={dateRange.month}
//         onChange={(e) => setDateRange({ ...dateRange, month: e.target.value })}
//         placeholder="Select Month"
//       />
//       <input
//         type="number"
//         value={dateRange.year}
//         onChange={(e) => setDateRange({ ...dateRange, year: e.target.value })}
//         placeholder="Select Year"
//       />

//       {/* Payroll Table */}
//       <table>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Salary</th>
//             <th>Brought Forward</th>
//             <th>Deduction Repayment</th>
//             <th>Deductions</th>
//             <th>Bonus</th>
//             <th>Net Pay</th>
//           </tr>
//         </thead>
//         <tbody>
//           {payrollData.map((employee) => (
//             <tr key={employee.userId}>
//               <td>{employee.name}</td>
//               <td>{employee.salary}</td>
//               <td>{employee.broughtForward}</td>
//               <td>{employee.repayment}</td>
//               <td>{employee.deductions}</td>
//               <td>{employee.bonus}</td>
//               <td>{employee.netPay}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Total Pay */}
//       <div>Total Pay: {totalPay}</div>

//       {/* Buttons for CSV and PDF Export */}
//       <button onClick={downloadCSV}>Download CSV</button>
//       <button onClick={downloadPDF}>Download PDF</button>
//     </div>
//   );
// };

// export default PayrollPage;

// import React, { useState, useEffect } from 'react';
// import { useContext } from 'react';
// import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// import AuthContext from '../../../../Context/AuthProvider';
// import { parse } from 'json2csv';
// import { jsPDF } from 'jspdf';

// const PayrollPage = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const [users, setUsers] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [payrollData, setPayrollData] = useState([]);
//   const [dateRange, setDateRange] = useState({ month: '', year: '' });
//   const { setAuth, auth } = useContext(AuthContext);

//   const calculateStatutoryDeductions = (baseSalary) => {
//     const NHIMA = baseSalary * 0.01; // 1% NHIMA
//     const NAPSA = baseSalary * 0.02; // 2% NAPSA
//     return { NHIMA, NAPSA };
//   };

//   const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus) => {
//     const { NHIMA, NAPSA } = calculateStatutoryDeductions(salary);
//     return salary - repayments - deductions - NHIMA - NAPSA + bonus + broughtForward;
//   };

//   useEffect(() => {
//     const getUsers = async () => {
//       try {
//         const response = await axiosPrivate.get('/users', {
//           params: { Org: auth.foundUser.Org },
//         });
//         setUsers(response.data);
//       } catch (err) {
//         console.error(err);
//       }
//     };
//     getUsers();
//   }, [auth]);

//   useEffect(() => {
//     const getTransactions = async () => {
//       try {
//         const response = await axiosPrivate.get('/txns', {
//           params: { Org: auth.foundUser.Org },
//         });
//         setTransactions(response.data.propdata);
//       } catch (err) {
//         console.error(err);
//       }
//     };
//     getTransactions();
//   }, [auth]);

//   useEffect(() => {
//     if (users.length > 0 && transactions.length > 0) {
//       const payroll = users.map(user => {
//         const filteredTransactions = transactions.filter(transaction => {
//           const transactionDate = new Date(transaction.date);
//           const transactionMonth = transactionDate.getMonth() + 1;
//           const transactionYear = transactionDate.getFullYear();
//           return (
//             (dateRange.month === '' || transactionMonth === parseInt(dateRange.month)) &&
//             (dateRange.year === '' || transactionYear === parseInt(dateRange.year))
//           );
//         });

//         const repaymentThisMonth = filteredTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction Repayment')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         const deductionsThisMonth = filteredTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         const bonusesThisMonth = filteredTransactions
//           .filter(transaction => transaction.transactionType === 'Allowance')
//           .reduce((total, transaction) => total + transaction.amount, 0);

//         const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth);

//         return {
//           userId: user.userId,
//           name: user.name,
//           salary: user.Base_Pay,
//           broughtForward: user.Advance_BF,
//           repayment: repaymentThisMonth,
//           deductions: deductionsThisMonth,
//           bonus: bonusesThisMonth,
//           netPay
//         };
//       });

//       setPayrollData(payroll);
//     }
//   }, [users, transactions, dateRange]);

//   const totalPay = payrollData.reduce((total, employee) => total + employee.netPay, 0);

//   const downloadCSV = () => {
//     const csv = parse(payrollData);
//     const blob = new Blob([csv], { type: 'text/csv' });
//     const link = document.createElement('a');
//     link.href = URL.createObjectURL(blob);
//     link.download = 'payroll.csv';
//     link.click();
//   };

//   const downloadPDF = () => {
//     const doc = new jsPDF();
//     doc.autoTable({
//       head: [['Name', 'Salary', 'Brought Forward', 'Deduction Repayment', 'Deductions', 'Bonus', 'Net Pay']],
//       body: payrollData.map(employee => [
//         employee.name,
//         employee.salary,
//         employee.broughtForward,
//         employee.repayment,
//         employee.deductions,
//         employee.bonus,
//         employee.netPay,
//       ]),
//     });
//     doc.save('payroll.pdf');
//   };

//   return (
//     <div className="payroll-page">
//       <h1>Payroll</h1>
//       <input
//         type="month"
//         value={dateRange.month}
//         onChange={(e) => setDateRange({ ...dateRange, month: e.target.value })}
//         placeholder="Select Month"
//       />
//       <input
//         type="number"
//         value={dateRange.year}
//         onChange={(e) => setDateRange({ ...dateRange, year: e.target.value })}
//         placeholder="Select Year"
//       />
//       <table>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Salary</th>
//             <th>Brought Forward</th>
//             <th>Deduction Repayment</th>
//             <th>Deductions</th>
//             <th>Bonus</th>
//             <th>Net Pay</th>
//           </tr>
//         </thead>
//         <tbody>
//           {payrollData.map((employee) => (
//             <tr key={employee.userId}>
//               <td>{employee.name}</td>
//               <td>{employee.salary}</td>
//               <td>{employee.broughtForward}</td>
//               <td>{employee.repayment}</td>
//               <td>{employee.deductions}</td>
//               <td>{employee.bonus}</td>
//               <td>{employee.netPay}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//       <div>Total Pay: {totalPay}</div>
//       <button onClick={downloadCSV}>Download CSV</button>
//       <button onClick={downloadPDF}>Download PDF</button>
//     </div>
//   );
// };

// export default PayrollPage;

// import React, { useState, useEffect } from 'react';
// import { useContext } from 'react';

// // import { axiosPrivate } from '../../../../api/axios';
// import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// import AuthContext from '../../../../Context/AuthProvider';
// // import AuthContext from "../../Context/AuthProvider";
// import "./Payroll.css"




// // import axiosPrivate from 'your-axios-instance'; // Replace with your actual axios instance
// // axiosPrivate

// // axiosPrivate

// // Helper function to calculate payroll values
// const calculateNetPay = (salary, broughtForward, repayments, deductions, bonus) => {
//   return salary  - repayments ;
// }

// const PayrollPage = () => {

//   const axiosPrivate =   useAxiosPrivate()
//   const [users, setUsers] = useState([]);
//   const [transactions, setTransactions] = useState([]);
//   const [payrollData, setPayrollData] = useState([]);
//   const { setAuth,auth } = useContext(AuthContext);

//   // Fetch users data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getUsers = async () => {
//       try {
//         const response = await axiosPrivate.get('/users', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org, // Your org parameter
//           },
//         });
//         if (isMounted) {
//           setUsers(response.data);
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getUsers();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Fetch transactions data
//   useEffect(() => {
//     let isMounted = true;
//     const controller = new AbortController();

//     const getTransactions = async () => {
//       try {
//         const response = await axiosPrivate.get('/txns', {
//           signal: controller.signal,
//           params: {
//             Org: auth.foundUser.Org, // Your org parameter
//           },
          
//         });
//         console.log("respon",response)
//         if (isMounted) {
//           setTransactions(response.data.propdata);
//           console.log("respon 2",response)
//         }
//       } catch (err) {
//         console.error(err);
//       }
//     };

//     getTransactions();
//     return () => {
//       isMounted = false;
//       controller.abort();
//     };
//   }, [auth]);

//   // Process data to calculate payroll after both users and transactions are loaded
//   useEffect(() => {
//     if (users.length > 0 && transactions.length > 0) {
//       const payroll = users.map(user => {
//         const userTransactions = transactions.filter(transaction => transaction.txnID == user._id);

//         // Sum up the repayment (deduction repayments are positive amounts)
//         //const options = ["Allowance","Deduction","Deduction Repayment"];
//         const repaymentThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction Repayment')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up the deductions (deductions are negative amounts)
//         const deductionsThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Deduction')
//           .reduce((total, transaction) => total + transaction.transactionAmount, 0);

//         // Sum up bonuses
//         const bonusesThisMonth = userTransactions
//           .filter(transaction => transaction.transactionType === 'Allowance')
//           .reduce((total, transaction) => total + transaction.amount, 0);

//         // Calculate net pay
//         const netPay = calculateNetPay(user.Base_Pay, user.Advance_BF, repaymentThisMonth, deductionsThisMonth, bonusesThisMonth);

//         return {
//           userId: user.userId,
//           name: user.name,
//           salary: user.Base_Pay,
//           broughtForward: user.Advance_BF,
//           repayment: repaymentThisMonth,
//           deductions: deductionsThisMonth,
//           bonus: bonusesThisMonth,
//           netPay

//         };
//       });

//       setPayrollData(payroll);
//     }
//   }, [users, transactions]);

//   return (
//     <div className="payroll-page">
//       <h1>Payroll</h1>
//       <table>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Salary</th>
//             <th>Brought Forward</th>
//             <th>Deduction Repayment</th>
//             <th>Deductions</th>
//             <th>Bonus</th>
//             <th>Net Pay</th>
//           </tr>
//         </thead>
//         <tbody>
//           {payrollData.map((employee) => (
//             <tr key={employee.userId}>
//               <td>{employee.name}</td>
//               <td>{employee.salary}</td>
//               <td>{employee.broughtForward}</td>
//               <td>{employee.repayment}</td>
//               <td>{employee.deductions}</td>
//               <td>{employee.bonus}</td>
//               <td>{employee.netPay}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// export default PayrollPage;
